/**
 * RETURN-TO-ORIGIN -- the navigation-context primitive.
 *
 * DOCTOR-CONSULT-CORRECTIVE-V1 (BUILD 3). L-09 ("Back to search" does not go
 * back to search) and the July finding `gap.f-02-back-to-leads-state` ("In-app
 * Back to Leads loses filtered list state") are ONE missing primitive with two
 * instances: a record reached from several places carries no memory of where
 * it was reached FROM, so its Back control can only point at a constant. The
 * label then says "back" while the behaviour says "go to a fixed page".
 *
 * Built ONCE here, consumed by the surface that supplies its own origin ->
 * door map. FIRST CONSUMER: the doctor patient record
 * (doctor-patient-lens-view.ts). SECOND CONSUMER, named and NOT wired by this
 * package: the leads list / Lead 360 (`gap.f-02-back-to-leads-state`) -- see
 * the run record's blast-radius count.
 *
 * WHY A TOKEN AND NEVER A URL: the origin travels in the query string, which
 * the operator (or anyone) can edit. An allowlisted TOKEN that the DESTINATION
 * maps to a known href cannot become an open redirect; a raw `?returnTo=<url>`
 * can. This mirrors the shipped `REGISTER_ORIGINS` / `parseRegisterOrigin`
 * idiom (patient-master-view.ts:982-989) rather than inventing a second shape.
 *
 * Pure. No router, no storage, no fetch -- the URL is the whole carrier, so a
 * reload, a shared link and a browser Back all agree.
 */

/** Where a Back control leads, and what it may honestly call itself. */
export interface OriginDoor {
  readonly href: string;
  readonly label: string;
}

/**
 * Narrow a raw `?from=` value to one of the surface's allowlisted tokens.
 * Anything else -- absent, empty, forged, or a URL someone typed in -- is
 * null, and the caller falls back to its honest default door.
 */
export function parseOriginToken<T extends string>(
  allowed: readonly T[],
  raw: string | null | undefined,
): T | null {
  const value = (raw ?? '').trim();
  if (value === '') return null;
  return (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

/**
 * Stamp the origin onto a destination href, so the destination's Back control
 * can resolve it. No origin = the href is returned byte-identical (every
 * existing caller keeps its current URL).
 */
export function withOrigin<T extends string>(
  href: string,
  origin?: T | null,
): string {
  if (origin === undefined || origin === null || origin === '') return href;
  const sep = href.includes('?') ? '&' : '?';
  return `${href}${sep}from=${encodeURIComponent(origin)}`;
}

/**
 * Resolve the Back door for the origin actually travelled.
 *
 * A resolver may return null when the origin is known but not REACHABLE on
 * this render (e.g. "came from the consult" with no appointment id resolved).
 * That degrades to the fallback door -- never a dead door (LES-016) -- and the
 * label always follows the BEHAVIOUR, never the intent, which is the whole
 * defect this primitive exists to end.
 */
export function resolveOriginDoor<T extends string>(
  resolvers: { readonly [K in T]: () => OriginDoor | null },
  origin: T | null,
  fallback: OriginDoor,
): OriginDoor {
  if (origin === null) return fallback;
  const resolver = resolvers[origin];
  if (resolver === undefined) return fallback;
  return resolver() ?? fallback;
}
